import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

export type TooltipAccent = 'purple' | 'amber' | 'cyan' | 'green' | 'red' | 'slate';

const ACCENT_STYLES: Record<TooltipAccent, { border: string; title: string; badgeBg: string; badgeText: string }> = {
  purple: {
    border: 'rgba(168, 85, 247, 0.4)',
    title: 'text-purple-300',
    badgeBg: 'bg-purple-500/20 border-purple-500/30',
    badgeText: 'text-purple-300',
  },
  amber: {
    border: 'rgba(245, 158, 11, 0.4)',
    title: 'text-amber-300',
    badgeBg: 'bg-amber-500/20 border-amber-500/30',
    badgeText: 'text-amber-300',
  },
  cyan: {
    border: 'rgba(6, 182, 212, 0.4)',
    title: 'text-cyan-300',
    badgeBg: 'bg-cyan-500/20 border-cyan-500/30',
    badgeText: 'text-cyan-300',
  },
  green: {
    border: 'rgba(16, 185, 129, 0.4)',
    title: 'text-emerald-300',
    badgeBg: 'bg-emerald-500/20 border-emerald-500/30',
    badgeText: 'text-emerald-300',
  },
  red: {
    border: 'rgba(239, 68, 68, 0.4)',
    title: 'text-red-300',
    badgeBg: 'bg-red-500/20 border-red-500/30',
    badgeText: 'text-red-300',
  },
  slate: {
    border: 'rgba(148, 163, 184, 0.35)',
    title: 'text-slate-200',
    badgeBg: 'bg-slate-500/20 border-slate-500/30',
    badgeText: 'text-slate-300',
  },
};

export interface BrewTooltipProps {
  title: string;
  badge?: string;
  description: React.ReactNode;
  detail?: React.ReactNode;
  accent?: TooltipAccent;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function BrewTooltip({
  title,
  badge,
  description,
  detail,
  accent = 'purple',
  children,
  className = 'inline-flex items-center',
  disabled = false,
}: BrewTooltipProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const triggerRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (disabled) return;
    clearTimeout(timerRef.current);
    if (triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [disabled]);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRect(null), 80);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const styleConfig = ACCENT_STYLES[accent] || ACCENT_STYLES.purple;

  // Compute viewport-safe position
  const renderPortal = () => {
    if (!rect || typeof document === 'undefined') return null;

    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const rawCenterX = rect.left + rect.width / 2;
    const clampedX = Math.max(145, Math.min(vw - 145, rawCenterX));
    const showBelow = rect.top < 115;
    const topPos = showBelow ? rect.bottom + 8 : rect.top - 8;

    return createPortal(
      <div
        style={{
          position: 'fixed',
          left: clampedX,
          top: topPos,
          transform: showBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
          zIndex: 99999,
          width: 'max-content',
          maxWidth: '270px',
          pointerEvents: 'none',
          animation: 'fadeIn 120ms ease-out',
        }}
      >
        <div
          className="px-2.5 py-2 rounded-lg text-[10px] leading-relaxed"
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(28, 18, 48, 0.98))',
            border: `1px solid ${styleConfig.border}`,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={`font-bold text-[10px] tracking-wide ${styleConfig.title}`}>
              {title}
            </span>
            {badge && (
              <span className={`px-1.5 py-0.2 rounded text-[8px] font-mono font-semibold border ${styleConfig.badgeBg} ${styleConfig.badgeText}`}>
                {badge}
              </span>
            )}
          </div>
          <div className="text-white/80 text-[9.5px] leading-snug">
            {description}
          </div>
          {detail && (
            <div className="text-white/50 font-mono text-[8.5px] border-t border-white/10 pt-1 mt-1 leading-tight">
              {detail}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={triggerRef}
      className={className}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {renderPortal()}
    </div>
  );
}
